import { z } from "zod";

/** FBA fulfillable-quantity evidence older than this is treated as stale, not authoritative. */
export const FBA_INVENTORY_FRESHNESS_HOURS = 26;
export const FBA_INVENTORY_FRESHNESS_MS = FBA_INVENTORY_FRESHNESS_HOURS * 60 * 60_000;

export const catalogCompletenessSchema = z.enum([
  "complete",
  "partial",
  "account-type-unknown",
  "unavailable",
]);
export type CatalogCompleteness = z.infer<typeof catalogCompletenessSchema>;

export const productCatalogStateSchema = z.object({
  completeness: catalogCompletenessSchema,
  source: z.enum(["sp-api", "ads-metadata", "unknown"]).optional(),
  // Only sellers can ever reach an `sp-api` catalog, so the UI needs the
  // account type to know whether an Ads-only catalog is fixable.
  accountType: z.enum(["seller", "vendor"]).nullable().optional(),
  lastSyncedAt: z.string().datetime().nullable(),
  lastAttemptAt: z.string().datetime().nullable(),
  isStale: z.boolean(),
  inventory: z
    .object({
      status: z.enum(["fresh", "stale", "failed", "never"]),
      lastSyncedAt: z.string().datetime().nullable(),
      lastAttemptAt: z.string().datetime().nullable(),
      errorCode: z.string().nullable(),
    })
    .optional(),
});
export type ProductCatalogState = z.infer<typeof productCatalogStateSchema>;

export const productStatusSchema = z.enum(["advertised", "unadvertised", "unavailable"]);
export type ProductStatus = z.infer<typeof productStatusSchema>;

export const productStatusFilterSchema = productStatusSchema.exclude(["unavailable"]);
export type ProductStatusFilter = z.infer<typeof productStatusFilterSchema>;

export const productListingRoleSchema = z.enum(["parent", "child", "standalone", "unknown"]);
export type ProductListingRole = z.infer<typeof productListingRoleSchema>;

export const productAdvertisingEligibilitySchema = z.enum([
  "unknown",
  "eligible",
  "eligible_with_warning",
  "ineligible",
]);
export type ProductAdvertisingEligibility = z.infer<typeof productAdvertisingEligibilitySchema>;
export const productEligibilityReasonSchema = z.object({
  name: z.string(),
  message: z.string().optional(),
  severity: z.enum(["INELIGIBLE", "ELIGIBLE_WITH_WARNING"]),
});
export type ProductEligibilityReason = z.infer<typeof productEligibilityReasonSchema>;
export const productEligibilityEvidenceFields = {
  eligibilityError: z.literal("not_returned").optional(),
  eligibility: productAdvertisingEligibilitySchema,
  eligibilityReasons: z.array(productEligibilityReasonSchema).optional(),
  eligibilityCheckedAt: z.string().datetime().nullable().optional(),
  eligibilitySource: z.literal("amazon-ads").nullable().optional(),
};

export interface ProductListingSelectionState {
  blockedReason: "parent" | "inactive" | "ineligible" | "out_of_stock" | "inventory_unknown" | null;
  warnings: ("inventory_unknown" | "out_of_stock" | "eligibility_unknown")[];
}

/** Catalog and inventory evidence never establishes Amazon advertising eligibility. */
export function getProductListingSelectionState(listing: {
  isActive: boolean;
  listingRole?: ProductListingRole;
  fulfillmentChannel?: "FBA" | "FBM" | "MIXED" | null;
  quantity?: number | null;
  fbaFulfillableQuantity?: number | null;
  effectiveInventoryQuantity?: number | null;
  eligibility?: ProductAdvertisingEligibility;
}): ProductListingSelectionState {
  const effectiveQuantity =
    "effectiveInventoryQuantity" in listing
      ? listing.effectiveInventoryQuantity
      : (listing.fulfillmentChannel === "FBA" || listing.fulfillmentChannel === "MIXED") &&
          listing.fbaFulfillableQuantity != null
        ? listing.fbaFulfillableQuantity
        : listing.quantity;
  const blockedReason = !listing.isActive
    ? "inactive"
    : listing.listingRole === "parent"
      ? "parent"
      : listing.eligibility === "ineligible"
        ? "ineligible"
        : effectiveQuantity == null
          ? "inventory_unknown"
          : effectiveQuantity <= 0
            ? "out_of_stock"
            : null;
  const warnings: ProductListingSelectionState["warnings"] = [];
  if (effectiveQuantity == null) warnings.push("inventory_unknown");
  else if (effectiveQuantity <= 0) warnings.push("out_of_stock");
  if (!listing.eligibility || listing.eligibility === "unknown")
    warnings.push("eligibility_unknown");
  return { blockedReason, warnings };
}

const paginationQueryFields = {
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
};

export const productListingQuerySchema = z.object({
  ...paginationQueryFields,
  search: z.string().trim().max(200).optional(),
  variationGroupKey: z.string().trim().min(1).max(64).optional(),
  active: z.enum(["true", "false", "all"]).default("true"),
  /** `unknown` selects SQL NULL; other values are exact Amazon values. */
  availability: z.string().trim().min(1).max(100).optional(),
  productStatus: productStatusFilterSchema.optional(),
  adProduct: z.literal("sp").optional(),
});
export type ProductListingQuery = z.infer<typeof productListingQuerySchema>;

export const productListingGroupQuerySchema = productListingQuerySchema
  .omit({
    variationGroupKey: true,
  })
  .extend({
    sortBy: z.enum(["type", "price", "inventory"]).optional(),
    sortOrder: z.enum(["asc", "desc"]).default("asc"),
  });
export type ProductListingGroupQuery = z.infer<typeof productListingGroupQuerySchema>;

export const productListingSchema = z.object({
  source: z.enum(["sp-api", "ads-metadata"]).optional(),
  listingRole: productListingRoleSchema.optional(),
  parentAsin: z.string().nullable().optional(),
  sku: z.string(),
  asin: z.string(),
  variationGroupKey: z.string(),
  title: z.string().nullable(),
  imageUrl: z.string().nullable(),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  bestSellerRank: z.string().nullable(),
  availability: z.string().nullable(),
  listingStatus: z.array(z.string()).optional(),
  fulfillmentChannel: z.enum(["FBA", "FBM", "MIXED"]).nullable().optional(),
  quantity: z.number().int().nonnegative().nullable().optional(),
  fbaFulfillableQuantity: z.number().int().nonnegative().nullable().optional(),
  fbaInventorySyncedAt: z.string().datetime().nullable().optional(),
  effectiveInventoryQuantity: z.number().int().nonnegative().nullable().optional(),
  inventorySources: z
    .array(
      z.object({
        channelCode: z.string(),
        sourceKind: z.enum(["amazon_fulfilled", "merchant_default", "merchant_location"]),
        sourceName: z.string().nullable(),
        quantity: z.number().int().nonnegative().nullable(),
        syncedAt: z.string().datetime(),
      }),
    )
    .optional(),
  priceAmount: z.string().nullable(),
  priceCurrency: z.string().nullable(),
  basisPriceAmount: z.string().nullable(),
  basisPriceCurrency: z.string().nullable(),
  isActive: z.boolean(),
  ...productEligibilityEvidenceFields,
  productStatus: productStatusSchema,
  firstSeenAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  syncedAt: z.string().datetime(),
});
export type ProductListing = z.infer<typeof productListingSchema>;

export const productListingGroupSingleListingSchema = z.object({
  sku: z.string(),
  asin: z.string(),
  listingRole: productListingRoleSchema.optional(),
  title: z.string().nullable(),
  imageUrl: z.string().nullable(),
  priceAmount: z.string().nullable(),
  priceCurrency: z.string().nullable(),
  fulfillmentChannel: z.enum(["FBA", "FBM", "MIXED"]).nullable().optional(),
  quantity: z.number().int().nonnegative().nullable(),
  fbaFulfillableQuantity: z.number().int().nonnegative().nullable(),
  effectiveInventoryQuantity: z.number().int().nonnegative().nullable().optional(),
  inventorySources: productListingSchema.shape.inventorySources,
  productStatus: productStatusSchema,
});
export type ProductListingGroupSingleListing = z.infer<
  typeof productListingGroupSingleListingSchema
>;

export const productListingGroupSchema = z.object({
  variationGroupKey: z.string(),
  groupKind: z.enum(["parent", "standalone", "unknown"]).optional(),
  parentAsin: z.string().nullable().optional(),
  title: z.string().nullable(),
  imageUrl: z.string().nullable(),
  activeSkuCount: z.number().int().nonnegative(),
  matchingSkuCount: z.number().int().nonnegative(),
  selectableSkuCount: z.number().int().nonnegative().optional(),
  childAsinCount: z.number().int().nonnegative(),
  singleListing: productListingGroupSingleListingSchema.nullable().optional(),
});
export type ProductListingGroup = z.infer<typeof productListingGroupSchema>;

export const productListingPaginationSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive().max(100),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

export const productListingListResponseSchema = z.object({
  data: z.array(productListingSchema),
  catalog: productCatalogStateSchema,
  pagination: productListingPaginationSchema,
});
export type ProductListingListResponse = z.infer<typeof productListingListResponseSchema>;

export const productListingGroupResponseSchema = z.object({
  data: z.array(productListingGroupSchema),
  catalog: productCatalogStateSchema,
  pagination: productListingPaginationSchema,
});
export type ProductListingGroupResponse = z.infer<typeof productListingGroupResponseSchema>;

export const resolveProductListingsRequestSchema = z.object({
  identifiers: z.array(z.string()).min(1).max(500),
  adProduct: z.literal("sp").optional(),
});

const resolvedListingFields = { identifier: z.string(), listing: productListingSchema };
export const productListingResolutionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("resolved"),
    matchedBy: z.enum(["sku", "asin"]),
    ...resolvedListingFields,
  }),
  z.object({
    type: z.literal("ambiguous"),
    identifier: z.string(),
    candidates: z.array(productListingSchema).min(2),
  }),
  z.object({
    type: z.literal("inactive"),
    matchedBy: z.enum(["sku", "asin"]),
    ...resolvedListingFields,
  }),
  z.object({ type: z.literal("invalid"), identifier: z.string() }),
  z.object({ type: z.literal("duplicate"), identifier: z.string(), duplicateOf: z.string() }),
]);
export type ProductListingResolution = z.infer<typeof productListingResolutionSchema>;

export const resolveProductListingsResponseSchema = z.object({
  results: z.array(productListingResolutionSchema),
  catalog: productCatalogStateSchema,
});
export type ResolveProductListingsResponse = z.infer<typeof resolveProductListingsResponseSchema>;

export const productListingRefreshResponseSchema = z.object({
  runId: z.string().uuid(),
  catalog: productCatalogStateSchema,
});

export const productListingIdentitySchema = z.object({
  sku: z.string().min(1),
  asin: z.string().min(1),
  variationGroupKey: z.string().min(1),
  title: z.string().nullable(),
  imageUrl: z.string().nullable(),
  ...productEligibilityEvidenceFields,
  eligibility: productAdvertisingEligibilitySchema.optional(),
});
export type ProductListingIdentity = z.infer<typeof productListingIdentitySchema>;

export const productListingFilterSnapshotSchema = z.object({
  search: z.string().trim().max(200).optional(),
  active: z.enum(["true", "false", "all"]).default("true"),
  availability: z.string().trim().min(1).max(100).optional(),
  productStatus: productStatusFilterSchema.optional(),
  adProduct: z.literal("sp").optional(),
});
export type ProductListingFilterSnapshot = z.infer<typeof productListingFilterSnapshotSchema>;

const selectedSkuSetSchema = z.array(z.string().trim().min(1).max(200));
export const productListingBulkIdentitiesRequestSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("select"),
    filters: productListingFilterSnapshotSchema,
    variationGroupKey: z.string().trim().min(1).max(64).optional(),
    selectedSkus: selectedSkuSetSchema,
  }),
  z.object({
    mode: z.literal("intersect"),
    filters: productListingFilterSnapshotSchema,
    selectedSkus: selectedSkuSetSchema,
  }),
]);
export type ProductListingBulkIdentitiesRequest = z.infer<
  typeof productListingBulkIdentitiesRequestSchema
>;

export const productListingBulkIdentitiesResponseSchema = z.object({
  mode: z.enum(["select", "intersect"]),
  totalMatching: z.number().int().nonnegative(),
  resultingCount: z.number().int().nonnegative(),
  overflow: z.boolean(),
  identities: z.array(productListingIdentitySchema),
});
export type ProductListingBulkIdentitiesResponse = z.infer<
  typeof productListingBulkIdentitiesResponseSchema
>;
